import styled from 'styled-components';
import {
  TextField,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  Flex,
  FlexColumn,
  IconButton,
} from '@elementary/common-ui/components';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import { useFormContext } from 'react-hook-form';
import { WarehouseType } from '@elementary/common-ui/types';

type Props = {
  index: number;
  projectName?: string;
  warehouseType: WarehouseType;
  isExisting: boolean;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
  isFirst?: boolean;
  expanded: boolean;
  onToggle: () => void;
};

const StyledAccordion = styled(Accordion)<{ $isFirst?: boolean }>`
  && {
    box-shadow: none;
    border: 1px solid ${({ theme }) => theme.palette.common.gray5};
    border-radius: ${({ $isFirst }) => ($isFirst ? '6px 6px 0 0' : '0')};
    margin-top: ${({ $isFirst }) => ($isFirst ? '0' : '-1px')};

    &::before {
      display: none;
    }
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)<{ $isFirst?: boolean }>`
  && {
    height: 56px;
    padding: 0 12px;
    background-color: ${({ theme }) => theme.palette.common.white};
    border-radius: ${({ $isFirst }) => ($isFirst ? '6px 6px 0 0' : '0')};

    .MuiAccordionSummary-content {
      display: flex;
      align-items: center;
      padding-left: 8px;
    }

    .MuiAccordionSummary-expandIconWrapper {
      order: -1;
      color: ${({ theme }) => theme.palette.common.black};
    }
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  && {
    padding: 11px 15px 24px;
    background-color: ${({ theme }) => theme.palette.common.gray15};
    border-top: 1px solid ${({ theme }) => theme.palette.common.gray5};
  }
`;

const TitleContainer = styled(Flex)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const ProjectTitle = styled(Typography)<{ $isEmpty?: boolean }>`
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  color: ${({ theme, $isEmpty }) =>
    $isEmpty ? theme.palette.common.gray11 : theme.palette.common.gray12};
`;

const ErrorDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.error.main};
  flex-shrink: 0;
`;

const RemoveButton = styled(IconButton)`
  && {
    padding: 4px;
    svg {
      width: 20px;
      height: 20px;
      color: ${({ theme }) => theme.palette.common.black};
    }

    &:hover {
      svg {
        color: ${({ theme }) => theme.palette.error.main};
      }
    }
  }
`;

const FieldsContainer = styled(FlexColumn)`
  gap: 16px;
`;

type DbtProjectFieldsInfo = {
  isDatabaseRequired: boolean;
  databaseLabel: string | null;
  databasePlaceholder: string | null;
  schemaLabel: string;
  schemaPlaceholder: string;
};

const getDbtProjectFieldsInfoByWarehouseType = (
  warehouseType: WarehouseType,
): DbtProjectFieldsInfo => {
  switch (warehouseType) {
    case 'bigquery':
      return {
        isDatabaseRequired: true,
        databaseLabel: 'Project',
        schemaLabel: 'Elementary dataset',
        databasePlaceholder: 'e.g. my-project',
        schemaPlaceholder: 'e.g. my_dataset_elementary',
      };
    case 'redshift':
    case 'postgres':
      return {
        isDatabaseRequired: true,
        databaseLabel: 'Database name',
        schemaLabel: 'Elementary schema',
        databasePlaceholder: 'e.g. analytics',
        schemaPlaceholder: 'e.g. [schema name]_elementary',
      };
    case 'databricks':
      return {
        isDatabaseRequired: false,
        databaseLabel: 'Catalog',
        schemaLabel: 'Elementary schema',
        databasePlaceholder: 'e.g. hive_metastore',
        schemaPlaceholder: 'e.g. [schema name]_elementary',
      };
    case 'athena':
      return {
        isDatabaseRequired: true,
        databaseLabel: 'Database',
        schemaLabel: 'Elementary schema',
        databasePlaceholder: 'e.g. awsdatacatalog',
        schemaPlaceholder: 'e.g. [schema name]_elementary',
      };
    case 'clickhouse':
      return {
        isDatabaseRequired: false,
        databaseLabel: null,
        schemaLabel: 'Elementary schema',
        databasePlaceholder: null,
        schemaPlaceholder: 'e.g. [schema name]_elementary',
      };
    case 'fabric':
      return {
        isDatabaseRequired: true,
        databaseLabel: 'Database',
        schemaLabel: 'Elementary schema',
        databasePlaceholder: 'e.g. my_warehouse',
        schemaPlaceholder: 'e.g. [schema name]_elementary',
      };
    case 'dremio':
      return {
        isDatabaseRequired: true,
        databaseLabel: 'Object storage',
        schemaLabel: 'Elementary object storage path',
        databasePlaceholder: '@username',
        schemaPlaceholder: 'e.g. path/to/storage',
      };
    case 'snowflake':
    default:
      return {
        isDatabaseRequired: true,
        databaseLabel: 'Database name',
        schemaLabel: 'Elementary schema',
        databasePlaceholder: 'e.g. ANALYTICS',
        schemaPlaceholder: 'e.g. [schema name]_elementary',
      };
  }
};

const DbtProjectCard = ({
  index,
  projectName,
  warehouseType,
  isExisting,
  onRemove,
  canRemove,
  disabled,
  isFirst,
  expanded,
  onToggle,
}: Props) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const {
    isDatabaseRequired,
    databaseLabel,
    databasePlaceholder,
    schemaLabel,
    schemaPlaceholder,
  } = getDbtProjectFieldsInfoByWarehouseType(warehouseType);

  const dbtProjectsErrors = errors.dbtProjects as
    | Record<string, Record<string, { message?: string }>>
    | undefined;

  const getError = (field: string) =>
    dbtProjectsErrors?.[index]?.[field]?.message as string | undefined;

  const hasErrors = dbtProjectsErrors?.[index] != null;
  const displayName = projectName?.trim() || 'Untitled project';
  const isEmpty = !projectName?.trim();

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <StyledAccordion
      $isFirst={isFirst}
      expanded={expanded}
      onChange={onToggle}
      disableGutters
    >
      <StyledAccordionSummary
        $isFirst={isFirst}
        expandIcon={<ExpandMoreIcon />}
      >
        <TitleContainer>
          <ProjectTitle $isEmpty={isEmpty}>{displayName}</ProjectTitle>
          {hasErrors && <ErrorDot />}
        </TitleContainer>
        {canRemove && (
          <RemoveButton onClick={handleRemoveClick} disabled={disabled}>
            <TrashIcon />
          </RemoveButton>
        )}
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <FieldsContainer>
          <FormField title="Project name" errorMessage={getError('name')}>
            <TextField
              {...register(`dbtProjects.${index}.name`, {
                required: requiredLabel,
              })}
              variant="outlined"
              placeholder="e.g. jaffle_shop"
              disabled={disabled}
              size="large"
            />
          </FormField>

          {databaseLabel && (
            <FormField
              title={databaseLabel}
              optional={!isDatabaseRequired}
              errorMessage={getError('database')}
            >
              <TextField
                {...register(
                  `dbtProjects.${index}.database`,
                  isDatabaseRequired
                    ? {
                        required: requiredLabel,
                      }
                    : undefined,
                )}
                variant="outlined"
                placeholder={databasePlaceholder ?? undefined}
                disabled={disabled || isExisting}
                size="large"
              />
            </FormField>
          )}

          <FormField title={schemaLabel} errorMessage={getError('schema')}>
            <TextField
              {...register(`dbtProjects.${index}.schema`, {
                required: requiredLabel,
              })}
              variant="outlined"
              placeholder={schemaPlaceholder}
              disabled={disabled || isExisting}
              size="large"
            />
          </FormField>
        </FieldsContainer>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default DbtProjectCard;
